import { lazy } from "react";
import { Navigate, Outlet, type RouteObject } from "react-router-dom";
import { User } from "src/api/Tmd.dtos";

//import AnalyticsTracker from "../components/common/AnalyticsTracker/AnalyticsTracker";

import {
    GeneralSettingsPageRedirect,
    HubspotSettingsPageRedirect,
    InstitutionDepartmentPageRedirect,
    InstitutionSettingsPageRedirect,
    ItemSettingsPageRedirect,
    QaSettingsPageRedirect,
    SettingsPageRedirect,
    TrialInstitutionDetailsPageRedirect,
    TrialInstitutionItemPageRedirect,
    TrialInstitutionItemsPageRedirect,
    TrialInstitutionNotePageRedirect,
    TrialInstitutionNotesPageRedirect,
    TrialInstitutionParticipantPageRedirect,
    TrialInstitutionParticipantsPageRedirect,
    TrialInstitutionPersonnelDetailsPageRedirect,
    TrialInstitutionPersonnelPageRedirect,
    TrialParticipantPageRedirect,
    TrialSettingsPageRedirect,
} from "./redirects";

import { RouteTrialIdentityProvider } from "../components/providers/data/TrialIdentityProvider";
import { RouteTrialInstitutionIdentityProvider } from "../components/providers/data/TrialInstitutionIdentityProvider";
import { RouteInstitutionIdentityProvider } from "src/components/providers/data/InstitutionIdentityProvider";
import { RouteGroupIdentityProvider } from "src/components/providers/data/GroupIdentityProvider";
import { RoutePersonnelIdentityProvider } from "src/components/providers/data/PersonnelIdentityProvider";
import { defaultLayoutContext, type ILayoutContext } from "../contexts/layouts/LayoutContext";
import { mainNavigationItems } from "./navigation";
import LayoutProvider from "../components/providers/layouts/LayoutProvider";
import DefaultLayout from "../components/layouts/DefaultLayout";

const TrialsPage = lazy(() => import("src/pages/trials/TrialsPage"));
const TrialPage = lazy(() => import("src/pages/trials/TrialPage"));
const TrialApiPage = lazy(() => import("src/pages/trials/TrialApiPage"));
const TrialMilestonesPage = lazy(() => import("src/pages/trials/TrialMilestonesPage"));
const TrialTrackersPage = lazy(() => import("src/pages/trials/trackers/TrialTrackersPage"));
const TrialTrackerPage = lazy(() => import("src/pages/trials/trackers/TrialTrackerPage"));
const TrialNotesPage = lazy(() => import("src/pages/trials/notes/TrialNotesPage"));
const TrialNotePage = lazy(() => import("src/pages/trials/notes/TrialNotePage"));
const TrialItemsPage = lazy(() => import("src/pages/trials/trialItems/TrialItemsPage"));
const TrialItemPage = lazy(() => import("src/pages/trials/trialItems/TrialItemPage"));
const TrialItemImportPage = lazy(() => import("src/pages/trials/trialItems/TrialItemImportPage"));
const TrialItemUpdatePage = lazy(() => import("src/pages/trials/trialItems/TrialItemUpdatePage"));
const TrialParticipantsPage = lazy(
    () => import("src/pages/trials/participants/TrialParticipantsPage"),
);
const TrialParticipantPage = lazy(
    () => import("src/pages/trials/participants/TrialParticipantPage"),
);
const TrialParticipantAdministrationCaseReportPage = lazy(
    () => import("src/pages/trials/participants/TrialParticipantAdministrationCaseReportPage"),
);
const TrialAccrualsPage = lazy(() => import("src/pages/trials/TrialAccrualsPage"));
const TrialPersonnelPage = lazy(() => import("src/pages/trials/personnel/TrialPersonnelPage"));
const TrialPersonnelCreatePage = lazy(
    () => import("src/pages/trials/personnel/TrialPersonnelCreatePage"),
);
const TrialPersonnelDetailsPage = lazy(
    () => import("src/pages/trials/personnel/TrialPersonnelDetailsPage"),
);
const TrialGroupsPage = lazy(() => import("src/pages/trials/groups/TrialGroupsPage"));
const TrialGroupPage = lazy(() => import("src/pages/trials/groups/TrialGroupPage"));
const TrialTreatmentsPage = lazy(() => import("src/pages/trials/treatments/TrialTreatmentsPage"));
const TrialTreatmentPage = lazy(() => import("src/pages/trials/treatments/TrialTreatmentPage"));
const TrialFormsPage = lazy(() => import("src/pages/trials/forms/TrialFormsPage"));
const TrialFormPage = lazy(() => import("src/pages/trials/forms/TrialFormPage"));
const TrialFormReportPage = lazy(() => import("src/pages/trials/forms/TrialFormReportPage"));
const TrialFormAdministrationCaseReportPage = lazy(
    () => import("src/pages/trials/forms/TrialFormAdministrationCaseReportPage"),
);
const TrialFormEntryPage = lazy(() => import("src/pages/trials/forms/TrialFormEntryPage"));
const TrialItemInstancesPage = lazy(() => import("src/pages/trials/items/TrialItemInstancesPage"));
const TrialItemInstancePage = lazy(() => import("src/pages/trials/items/TrialItemInstancePage"));
const TrialItemInstanceVersionEntryPage = lazy(
    () => import("src/pages/trials/items/TrialItemInstanceVersionEntryPage"),
);
const TrialItemInstanceImportPage = lazy(
    () => import("src/pages/trials/items/TrialItemInstanceImportPage"),
);
const TrialInstitutionsPage = lazy(
    () => import("src/pages/trials/institutions/TrialInstitutionsPage"),
);
const TrialInstitutionPage = lazy(
    () => import("src/pages/trials/institutions/TrialInstitutionPage"),
);
const TrialInstitutionParticipantPage = lazy(
    () => import("src/pages/trials/institutions/participants/TrialInstitutionParticipantPage"),
);
const TrialInstitutionParticipantsPage = lazy(
    () => import("src/pages/trials/institutions/participants/TrialInstitutionParticipantsPage"),
);
const TrialInstitutionItemsPage = lazy(
    () => import("src/pages/trials/institutions/items/TrialInstitutionItemsPage"),
);
const TrialInstitutionItemPage = lazy(
    () => import("src/pages/trials/institutions/items/TrialInstitutionItemPage"),
);
const TrialInstitutionItemVersionEntryPage = lazy(
    () => import("src/pages/trials/institutions/items/TrialInstitutionItemVersionEntryPage"),
);
const TrialInstitutionPersonnelPage = lazy(
    () => import("src/pages/trials/institutions/personnel/TrialInstitutionPersonnelPage"),
);
const TrialInstitutionPersonnelCreate = lazy(
    () => import("src/pages/trials/institutions/personnel/TrialInstitutionPersonnelCreate"),
);
const TrialInstitutionPersonnelDetailsPage = lazy(
    () => import("src/pages/trials/institutions/personnel/TrialInstitutionPersonnelDetailsPage"),
);
const TrialInstitutionNotesPage = lazy(
    () => import("src/pages/trials/institutions/notes/TrialInstitutionNotesPage"),
);
const TrialInstitutionNotePage = lazy(
    () => import("src/pages/trials/institutions/notes/TrialInstitutionNotePage"),
);
const InstitutionsPage = lazy(() => import("src/pages/institutions/InstitutionsPage"));
const InstitutionPage = lazy(() => import("src/pages/institutions/InstitutionPage"));
const InstitutionFormsPage = lazy(
    () => import("src/pages/institutions/forms/InstitutionFormsPage"),
);
const InstitutionFormPage = lazy(() => import("src/pages/institutions/forms/InstitutionFormPage"));
const InstitutionFormEntryPage = lazy(
    () => import("src/pages/institutions/forms/InstitutionFormEntryPage"),
);
const InstitutionNotesPage = lazy(
    () => import("src/pages/institutions/notes/InstitutionNotesPage"),
);
const InstitutionNotePage = lazy(() => import("src/pages/institutions/notes/InstitutionNotePage"));
const InstitutionPersonnelPage = lazy(
    () => import("src/pages/institutions/personnel/InstitutionPersonnelPage"),
);
const InstitutionPersonnelEditPage = lazy(
    () => import("src/pages/institutions/personnel/InstitutionPersonnelEditPage"),
);
const InstitutionPersonnelCreatePage = lazy(
    () => import("src/pages/institutions/personnel/InstitutionPersonnelCreatePage"),
);
const InstitutionDepartmentsPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentsPage"),
);
const InstitutionDepartmentEditPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentEditPage"),
);
const InstitutionDepartmentPersonnelEditPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentPersonnelEditPage"),
);
const InstitutionDepartmentPersonnelCreatePage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentPersonnelCreatePage"),
);
const InstitutionDepartmentContactEditPage = lazy(
    () => import("src/pages/institutions/departments/InstitutionDepartmentContactEditPage"),
);
const GroupsPage = lazy(() => import("src/pages/groups/GroupsPage"));
const GroupPage = lazy(() => import("src/pages/groups/GroupPage"));
const GroupNotesPage = lazy(() => import("src/pages/groups/notes/GroupNotesPage"));
const GroupNotePage = lazy(() => import("src/pages/groups/notes/GroupNotePage"));
const GroupPersonnelPage = lazy(() => import("src/pages/groups/personnel/GroupPersonnelPage"));
const GroupPersonnelEditPage = lazy(
    () => import("src/pages/groups/personnel/GroupPersonnelEditPage"),
);
const GroupTrialsPage = lazy(() => import("src/pages/groups/trials/GroupTrialsPage"));
const GroupItemsPage = lazy(() => import("src/pages/groups/items/GroupItemsPage"));
const GroupItemPage = lazy(() => import("src/pages/groups/items/GroupItemPage"));
const PersonnelPage = lazy(() => import("src/pages/personnel/PersonnelPage"));
const PersonnelByRolesPage = lazy(() => import("src/pages/personnel/roles/PersonnelByRolesPage"));
const PersonnelDetailsPage = lazy(() => import("src/pages/personnel/PersonnelDetailsPage"));
const PersonnelNotesPage = lazy(() => import("src/pages/personnel/notes/PersonnelNotesPage"));
const PersonnelNotePage = lazy(() => import("src/pages/personnel/notes/PersonnelNotePage"));
const PersonnelRolesPage = lazy(() => import("src/pages/personnel/roles/PersonnelRolesPage"));
const PersonnelRoleEditPage = lazy(() => import("src/pages/personnel/roles/PersonnelRoleEditPage"));
const PersonnelActiveDirectoryPage = lazy(
    () => import("src/pages/personnel/activeDirectory/PersonnelActiveDirectoryPage"),
);
const PersonnelOCRolesPage = lazy(
    () => import("src/pages/personnel/openClinica/PersonnelOCRolesPage"),
);
const PersonnelPermissionsPage = lazy(
    () => import("src/pages/personnel/permissions/PersonnelPermissionsPage"),
);
const PersonnelFilesPage = lazy(() => import("src/pages/personnel/files/PersonnelFilesPage"));
const PersonnelHubspotPage = lazy(() => import("src/pages/personnel/hubspot/PersonnelHubspotPage"));
const CountriesPage = lazy(() => import("src/pages/settings/general/countries/CountriesPage"));
const CountryPage = lazy(() => import("src/pages/settings/general/countries/CountryPage"));
const StatesPage = lazy(() => import("src/pages/settings/general/states/StatesPage"));
const StatePage = lazy(() => import("src/pages/settings/general/states/StatePage"));
const ItemComponentTypesPage = lazy(
    () => import("src/pages/settings/general/itemComponentTypes/ItemComponentTypesPage"),
);
const ItemComponentTypePage = lazy(
    () => import("src/pages/settings/general/itemComponentTypes/ItemComponentTypePage"),
);
const NoteTypesPage = lazy(() => import("src/pages/settings/general/noteTypes/NoteTypesPage"));
const NoteTypePage = lazy(() => import("src/pages/settings/general/noteTypes/NoteTypePage"));
const GendersPage = lazy(() => import("src/pages/settings/general/genders/GendersPage"));
const GenderPage = lazy(() => import("src/pages/settings/general/genders/GenderPage"));
const InterestsPage = lazy(() => import("src/pages/settings/general/interests/InterestsPage"));
const InterestPage = lazy(() => import("src/pages/settings/general/interests/InterestPage"));
const MembershipTypesPage = lazy(
    () => import("src/pages/settings/general/membershipTypes/MembershipTypesPage"),
);
const MembershipTypePage = lazy(
    () => import("src/pages/settings/general/membershipTypes/MembershipTypePage"),
);
const ProfessionalDisciplinesPage = lazy(
    () => import("src/pages/settings/general/professionalDisciplines/ProfessionalDisciplinesPage"),
);
const ProfessionalDisciplinePage = lazy(
    () => import("src/pages/settings/general/professionalDisciplines/ProfessionalDisciplinePage"),
);
const SalutationsPage = lazy(
    () => import("src/pages/settings/general/salutations/SalutationsPage"),
);
const SalutationPage = lazy(() => import("src/pages/settings/general/salutations/SalutationPage"));
const RolesPage = lazy(() => import("src/pages/settings/general/roles/RolesPage"));
const RolePage = lazy(() => import("src/pages/settings/general/roles/RolePage"));
const HubspotActiveMembersPage = lazy(
    () => import("src/pages/settings/hubspot/HubspotActiveMembersPage"),
);
const HubspotExportPage = lazy(() => import("src/pages/settings/hubspot/HubspotExportPage"));
const HubspotMemberPage = lazy(() => import("src/pages/settings/hubspot/HubspotMemberPage"));
const OpenClinicaUserRolesPage = lazy(
    () => import("src/pages/settings/openclinica/OpenClinicaUserRolesPage"),
);
const SiteLocationsPage = lazy(
    () => import("src/pages/settings/institution/siteLocations/SiteLocationsPage"),
);
const SiteLocationPage = lazy(
    () => import("src/pages/settings/institution/siteLocations/SiteLocationPage"),
);
const FacilityAllianceMembersPage = lazy(
    () =>
        import("src/pages/settings/institution/facilityAllianceMembers/FacilityAllianceMembersPage"),
);
const FacilityAllianceMemberPage = lazy(
    () =>
        import("src/pages/settings/institution/facilityAllianceMembers/FacilityAllianceMemberPage"),
);
const InstitutionTypesPage = lazy(
    () => import("src/pages/settings/institution/institutionTypes/InstitutionTypesPage"),
);
const InstitutionTypePage = lazy(
    () => import("src/pages/settings/institution/institutionTypes/InstitutionTypePage"),
);
const InstitutionCodingsPage = lazy(
    () => import("src/pages/settings/institution/institutionCodings/InstitutionCodingsPage"),
);
const InstitutionCodingPage = lazy(
    () => import("src/pages/settings/institution/institutionCodings/InstitutionCodingPage"),
);
const TrialCategoriesPage = lazy(
    () => import("src/pages/settings/trial/trialCategories/TrialCategoriesPage"),
);
const TrialCategoryPage = lazy(
    () => import("src/pages/settings/trial/trialCategories/TrialCategoryPage"),
);
const TrialTypesPage = lazy(() => import("src/pages/settings/trial/trialTypes/TrialTypesPage"));
const TrialTypePage = lazy(() => import("src/pages/settings/trial/trialTypes/TrialTypePage"));
const TrialPhasesPage = lazy(() => import("src/pages/settings/trial/trialPhases/TrialPhasesPage"));
const TrialPhasePage = lazy(() => import("src/pages/settings/trial/trialPhases/TrialPhasePage"));
const TumourGroupsPage = lazy(
    () => import("src/pages/settings/trial/tumourGroups/TumourGroupsPage"),
);
const TumourGroupPage = lazy(() => import("src/pages/settings/trial/tumourGroups/TumourGroupPage"));
const TrialPresentationTypesPage = lazy(
    () => import("src/pages/settings/trial/trialPresentationTypes/TrialPresentationTypesPage"),
);
const TrialPresentationTypePage = lazy(
    () => import("src/pages/settings/trial/trialPresentationTypes/TrialPresentationTypePage"),
);
const TrialPublicationTypesPage = lazy(
    () => import("src/pages/settings/trial/trialPublicationTypes/TrialPublicationTypesPage"),
);
const TrialPublicationTypePage = lazy(
    () => import("src/pages/settings/trial/trialPublicationTypes/TrialPublicationTypePage"),
);
const TrialAnalysisTypesPage = lazy(
    () => import("src/pages/settings/trial/trialAnalysisTypes/TrialAnalysisTypesPage"),
);
const TrialAnalysisTypePage = lazy(
    () => import("src/pages/settings/trial/trialAnalysisTypes/TrialAnalysisTypePage"),
);
const InvolvementsPage = lazy(
    () => import("src/pages/settings/trial/involvements/InvolvementsPage"),
);
const InvolvementPage = lazy(() => import("src/pages/settings/trial/involvements/InvolvementPage"));
const AllocatedWPsPage = lazy(
    () => import("src/pages/settings/trial/allocatedWPs/AllocatedWPsPage"),
);
const AllocatedWPPage = lazy(() => import("src/pages/settings/trial/allocatedWPs/AllocatedWPPage"));
const FormTemplatesPage = lazy(
    () => import("src/pages/settings/qa/formTemplates/FormTemplatesPage"),
);
const FormTemplatePage = lazy(() => import("src/pages/settings/qa/formTemplates/FormTemplatePage"));
const QAEmailTemplatesPage = lazy(
    () => import("src/pages/settings/qa/emailTemplates/QAEmailTemplatesPage"),
);
const QAEmailTemplatePage = lazy(
    () => import("src/pages/settings/qa/emailTemplates/QAEmailTemplatePage"),
);
const QAItemComponentsPage = lazy(
    () => import("src/pages/settings/qa/itemComponents/QAItemComponentsPage"),
);
const QAItemComponentPage = lazy(
    () => import("src/pages/settings/qa/itemComponents/QAItemComponentPage"),
);
const ReportingTagCategoriesPage = lazy(
    () => import("src/pages/settings/qa/reportingTagCategories/ReportingTagCategoriesPage"),
);
const ReportingTagCategoryPage = lazy(
    () => import("src/pages/settings/qa/reportingTagCategories/ReportingTagCategoryPage"),
);
const ReportingTagsPage = lazy(
    () => import("src/pages/settings/qa/reportingTags/ReportingTagsPage"),
);
const ReportingTagPage = lazy(() => import("src/pages/settings/qa/reportingTags/ReportingTagPage"));
const TaskTypesPage = lazy(() => import("src/pages/settings/qa/taskTypes/TaskTypesPage"));
const TaskTypePage = lazy(() => import("src/pages/settings/qa/taskTypes/TaskTypePage"));
const TrackerTypesPage = lazy(() => import("src/pages/settings/qa/trackerTypes/TrackerTypesPage"));
const TrackerTypePage = lazy(() => import("src/pages/settings/qa/trackerTypes/TrackerTypePage"));
const TrackerMilestonesPage = lazy(
    () => import("src/pages/settings/qa/trackerMilestones/TrackerMilestonesPage"),
);
const TrackerMilestonePage = lazy(
    () => import("src/pages/settings/qa/trackerMilestones/TrackerMilestonePage"),
);
const FormTemplateFileTypesPage = lazy(
    () => import("src/pages/settings/qa/formTemplateFileTypes/FormTemplateFileTypesPage"),
);
const FormTemplateFileTypePage = lazy(
    () => import("src/pages/settings/qa/formTemplateFileTypes/FormTemplateFileTypePage"),
);
const ItemComponentsPage = lazy(
    () => import("src/pages/settings/items/itemComponents/GeneralItemComponentsPage"),
);
const ItemComponentPage = lazy(
    () => import("src/pages/settings/items/itemComponents/GeneralItemComponentPage"),
);
const CategoriesPage = lazy(() => import("src/pages/settings/items/categories/CategoriesPage"));
const CategoryPage = lazy(() => import("src/pages/settings/items/categories/CategoryPage"));
const TrialInstitutionItemTemplatesPage = lazy(
    () =>
        import("src/pages/settings/items/trialInstitutionItemTemplates/TrialInstitutionItemTemplatesPage"),
);
const TrialInstitutionItemTemplatePage = lazy(
    () =>
        import("src/pages/settings/items/trialInstitutionItemTemplates/TrialInstitutionItemTemplatePage"),
);
const TemplateTrialItemsPage = lazy(
    () => import("src/pages/settings/items/templateTrialItems/TemplateTrialItemsPage"),
);
const TemplateTrialItemPage = lazy(
    () => import("src/pages/settings/items/templateTrialItems/TemplateTrialItemPage"),
);
const DashboardPage = lazy(() => import("src/pages/DashboardPage"));
const MyTrialsPage = lazy(() => import("src/pages/MyTrialsPage"));
const ErrorPage = lazy(() => import("src/pages/ErrorPage"));
const SearchPage = lazy(() => import("src/pages/SearchPage"));
const HomePage = lazy(() => import("src/pages/HomePage"));

import TrialSubMenu from "src/components/layouts/subMenus/TrialSubMenu";
import TrialInstitutionSubMenu from "src/components/layouts/subMenus/TrialInstitutionSubMenu";
import InstitutionSubMenu from "src/components/layouts/subMenus/InstitutionSubMenu";
import GroupSubMenu from "src/components/layouts/subMenus/GroupSubMenu";
import PersonnelSubMenu from "src/components/layouts/subMenus/PersonnelSubMenu";
import SettingsSubMenu from "src/components/layouts/subMenus/SettingsSubMenu";
import GeneralSettingsSubMenu from "src/components/layouts/subMenus/GeneralSettingsSubMenu";
import HubspotSettingsSubMenu from "src/components/layouts/subMenus/HubspotSettingsSubMenu";
import InstitutionSettingsSubMenu from "src/components/layouts/subMenus/InstitutionSettingsSubMenu";
import TrialSettingsSubMenu from "src/components/layouts/subMenus/TrialSettingsSubMenu";
import QaSettingsSubMenu from "src/components/layouts/subMenus/QaSettingsSubMenu";
import ItemSettingsSubMenu from "src/components/layouts/subMenus/ItemSettingsSubMenu";
import PatientSubMenu from "src/components/layouts/subMenus/PatientSubMenu";
import { RoutePatientIdentityProvider } from "src/components/providers/data/PatientIdentityProvider";
import DepartmentSubMenu from "src/components/layouts/subMenus/DepartmentSubMenu";
import { RouteDepartmentIdentityProvider } from "src/components/providers/data/DepartmentIdentityProvider";
import InstitutionDepartmentPersonnelPage from "src/pages/institutions/departments/InstitutionDepartmentPersonnelPage";

const createTrialRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "trials",
            children: [
                {
                    index: true,
                    element: <TrialsPage />,
                },
                {
                    path: "create",
                    element: <TrialPage />,
                },
                {
                    path: ":trialId",
                    element: (
                        <RouteTrialIdentityProvider>
                            <TrialSubMenu />
                            <Outlet />
                        </RouteTrialIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <TrialPage />,
                        },
                        {
                            path: "api",
                            element: <TrialApiPage />,
                        },
                        {
                            path: "milestones",
                            element: <TrialMilestonesPage />,
                        },
                        {
                            path: "trackers",
                            children: [
                                {
                                    index: true,
                                    element: <TrialTrackersPage />,
                                },
                                {
                                    path: ":trialTrackerId",
                                    element: <TrialTrackerPage />,
                                },
                            ],
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialNotesPage />,
                                },
                                {
                                    path: ":noteId",
                                    element: <TrialNotePage />,
                                },
                            ],
                        },
                        {
                            path: "trialitems",
                            children: [
                                {
                                    index: true,
                                    element: <TrialItemsPage />,
                                },
                                {
                                    path: ":trialItemId",
                                    element: <TrialItemPage />,
                                },
                            ],
                        },
                        {
                            path: "trialitemimport",
                            element: <TrialItemImportPage />,
                        },
                        {
                            path: "trialitemupdate",
                            element: <TrialItemUpdatePage />,
                        },
                        {
                            path: "participants",
                            children: [
                                {
                                    index: true,
                                    element: <TrialParticipantsPage />,
                                },
                                {
                                    path: "create",
                                    element: <TrialParticipantPage />,
                                },
                                {
                                    path: ":patientId",
                                    element: (
                                        <RoutePatientIdentityProvider>
                                            <PatientSubMenu />
                                            <Outlet />
                                        </RoutePatientIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialParticipantPageRedirect />,
                                        },
                                        {
                                            path: "details",
                                            element: <TrialParticipantPage />,
                                        },
                                        {
                                            path: "administrationcasereport",
                                            element: (
                                                <TrialParticipantAdministrationCaseReportPage />
                                            ),
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "accruals",
                            element: <TrialAccrualsPage />,
                        },
                        {
                            path: "personnel",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPersonnelPage />,
                                },
                                {
                                    path: "create",
                                    element: <TrialPersonnelCreatePage />,
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <TrialPersonnelDetailsPage />,
                                },
                            ],
                        },
                        {
                            path: "groups",
                            children: [
                                {
                                    index: true,
                                    element: <TrialGroupsPage />,
                                },
                                {
                                    path: ":trialGroupId",
                                    element: <TrialGroupPage />,
                                },
                            ],
                        },
                        {
                            path: "treatments",
                            children: [
                                {
                                    index: true,
                                    element: <TrialTreatmentsPage />,
                                },
                                {
                                    path: ":trialTreatmentId",
                                    element: <TrialTreatmentPage />,
                                },
                            ],
                        },
                        {
                            path: "forms",
                            children: [
                                {
                                    index: true,
                                    element: <TrialFormsPage />,
                                },
                                {
                                    path: ":trialFormId",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialFormPage />,
                                        },
                                        {
                                            path: "report",
                                            element: <TrialFormReportPage />,
                                        },
                                        {
                                            path: "administrationcasereport",
                                            element: <TrialFormAdministrationCaseReportPage />,
                                        },
                                        {
                                            path: "entry",
                                            element: <TrialFormEntryPage />,
                                        },
                                        {
                                            path: "versions/:trialFormVersionId/entry",
                                            element: <TrialFormEntryPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "items",
                            children: [
                                {
                                    index: true,
                                    element: <TrialItemInstancesPage />,
                                },
                                {
                                    path: ":trialItemInstanceId",
                                    children: [
                                        {
                                            index: true,
                                            element: <TrialItemInstancePage />,
                                        },
                                        {
                                            path: "entry",
                                            element: <TrialItemInstanceVersionEntryPage />,
                                        },
                                        {
                                            path: "versions/:trialItemInstanceVersionId/entry",
                                            element: <TrialItemInstanceVersionEntryPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "itemsimport",
                            element: <TrialItemInstanceImportPage />,
                        },
                        {
                            path: "institutions",
                            children: [
                                {
                                    index: true,
                                    element: <TrialInstitutionsPage />,
                                },
                                {
                                    path: "create",
                                    element: <TrialInstitutionPage />,
                                },
                                {
                                    path: ":trialInstitutionId",
                                    element: (
                                        <RouteTrialInstitutionIdentityProvider>
                                            <TrialInstitutionSubMenu />
                                            <Outlet />
                                        </RouteTrialInstitutionIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            path: "details",
                                            element: <TrialInstitutionPage />,
                                        },
                                        {
                                            path: "participants",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionParticipantsPage />,
                                                },
                                                {
                                                    path: "create",
                                                    element: <TrialInstitutionParticipantPage />,
                                                },
                                                {
                                                    path: ":patientId",
                                                    element: (
                                                        <RoutePatientIdentityProvider>
                                                            <PatientSubMenu />
                                                            <Outlet />
                                                        </RoutePatientIdentityProvider>
                                                    ),
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: (
                                                                <TrialInstitutionParticipantPageRedirect />
                                                            ),
                                                        },
                                                        {
                                                            path: "details",
                                                            element: (
                                                                <TrialInstitutionParticipantPage />
                                                            ),
                                                        },
                                                        {
                                                            path: "administrationcasereport",
                                                            element: (
                                                                <TrialParticipantAdministrationCaseReportPage />
                                                            ),
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            path: "items",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionItemsPage />,
                                                },
                                                {
                                                    path: ":trialInstitutionItemId",
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <TrialInstitutionItemPage />,
                                                        },
                                                        {
                                                            path: "entry",
                                                            element: (
                                                                <TrialInstitutionItemVersionEntryPage />
                                                            ),
                                                        },
                                                        {
                                                            path: "versions/:trialInstitutionItemVersionId/entry",
                                                            element: (
                                                                <TrialInstitutionItemVersionEntryPage />
                                                            ),
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            path: "personnel",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionPersonnelPage />,
                                                },
                                                {
                                                    path: "create",
                                                    element: <TrialInstitutionPersonnelCreate />,
                                                },
                                                {
                                                    path: ":personnelRoleId",
                                                    element: (
                                                        <TrialInstitutionPersonnelDetailsPage />
                                                    ),
                                                },
                                            ],
                                        },
                                        {
                                            path: "notes",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionNotesPage />,
                                                },
                                                {
                                                    path: ":noteId",
                                                    element: <TrialInstitutionNotePage />,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "trialinstitution",
                            children: [
                                {
                                    path: ":trialInstitutionId",
                                    children: [
                                        {
                                            path: "details",
                                            element: <TrialInstitutionDetailsPageRedirect />,
                                        },
                                        {
                                            path: "participants",
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <TrialInstitutionParticipantsPageRedirect />
                                                    ),
                                                },
                                                {
                                                    path: ":patientId",
                                                    element: (
                                                        <TrialInstitutionParticipantPageRedirect />
                                                    ),
                                                },
                                            ],
                                        },
                                        {
                                            path: "items",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionItemsPageRedirect />,
                                                },
                                                {
                                                    path: ":trialInstitutionItemId",
                                                    element: <TrialInstitutionItemPageRedirect />,
                                                },
                                            ],
                                        },
                                        {
                                            path: "personnel",
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <TrialInstitutionPersonnelPageRedirect />
                                                    ),
                                                },
                                                {
                                                    path: ":personnelRoleId",
                                                    element: (
                                                        <TrialInstitutionPersonnelDetailsPageRedirect />
                                                    ),
                                                },
                                            ],
                                        },
                                        {
                                            path: "notes",
                                            children: [
                                                {
                                                    index: true,
                                                    element: <TrialInstitutionNotesPageRedirect />,
                                                },
                                                {
                                                    path: ":noteId",
                                                    element: <TrialInstitutionNotePageRedirect />,
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ];

    return routes;
};

const createInstitutionRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "institutions",
            children: [
                {
                    index: true,
                    element: <InstitutionsPage />,
                },
                {
                    path: "create",
                    element: <InstitutionPage />,
                },
                {
                    path: ":institutionId",
                    element: (
                        <RouteInstitutionIdentityProvider>
                            <InstitutionSubMenu />
                            <Outlet />
                        </RouteInstitutionIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <InstitutionPage />,
                        },
                        {
                            path: "forms",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionFormsPage />,
                                },
                                {
                                    path: ":institutionFormId",
                                    children: [
                                        {
                                            index: true,
                                            element: <InstitutionFormPage />,
                                        },
                                        {
                                            path: "entry",
                                            element: <InstitutionFormEntryPage />,
                                        },
                                        {
                                            path: "versions/:institutionFormVersionId/entry",
                                            element: <InstitutionFormEntryPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionNotesPage />,
                                },
                                {
                                    path: ":noteId",
                                    element: <InstitutionNotePage />,
                                },
                            ],
                        },
                        {
                            path: "personnel",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionPersonnelPage />,
                                },
                                {
                                    path: "create",
                                    element: <InstitutionPersonnelCreatePage />,
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <InstitutionPersonnelEditPage />,
                                },
                            ],
                        },
                        {
                            path: "departments",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionDepartmentsPage />,
                                },
                                {
                                    path: "create",
                                    element: <InstitutionDepartmentEditPage />,
                                },
                                {
                                    path: ":departmentId",
                                    element: (
                                        <RouteDepartmentIdentityProvider>
                                            <DepartmentSubMenu />
                                            <Outlet />
                                        </RouteDepartmentIdentityProvider>
                                    ),
                                    children: [
                                        {
                                            index: true,
                                            element: <InstitutionDepartmentPageRedirect />,
                                        },
                                        {
                                            path: "details",
                                            element: <InstitutionDepartmentEditPage />,
                                        },
                                        {
                                            path: "personnel",
                                            element: <InstitutionDepartmentPersonnelPage />,
                                        },
                                        {
                                            path: "personnel/create",
                                            element: <InstitutionDepartmentPersonnelCreatePage />,
                                        },
                                        {
                                            path: "personnel/:personnelRoleId",
                                            element: <InstitutionDepartmentPersonnelEditPage />,
                                        },
                                        {
                                            path: "contacts/:departmentContactId",
                                            element: <InstitutionDepartmentContactEditPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ];

    return routes;
};

const createGroupRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "groups",
            children: [
                {
                    index: true,
                    element: <GroupsPage />,
                },
                {
                    path: ":groupId",
                    element: (
                        <RouteGroupIdentityProvider>
                            <GroupSubMenu />
                            <Outlet />
                        </RouteGroupIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <GroupPage />,
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <GroupNotesPage />,
                                },
                                {
                                    path: ":noteId",
                                    element: <GroupNotePage />,
                                },
                            ],
                        },
                        {
                            path: "personnel",
                            children: [
                                {
                                    index: true,
                                    element: <GroupPersonnelPage />,
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <GroupPersonnelEditPage />,
                                },
                            ],
                        },
                        {
                            path: "trials",
                            element: <GroupTrialsPage />,
                        },
                        {
                            path: "groupItems",
                            children: [
                                {
                                    index: true,
                                    element: <GroupItemsPage />,
                                },
                                {
                                    path: ":groupItemId",
                                    element: <GroupItemPage />,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ];

    return routes;
};

const createPersonnelRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "personnel",
            children: [
                {
                    index: true,
                    element: <PersonnelPage />,
                },
                {
                    path: "byroles",
                    element: <PersonnelByRolesPage />,
                },
                {
                    path: "create",
                    element: <PersonnelDetailsPage />,
                },
                {
                    path: ":personnelId",
                    element: (
                        <RoutePersonnelIdentityProvider>
                            <PersonnelSubMenu />
                            <Outlet />
                        </RoutePersonnelIdentityProvider>
                    ),
                    children: [
                        {
                            path: "details",
                            element: <PersonnelDetailsPage />,
                        },
                        {
                            path: "notes",
                            children: [
                                {
                                    index: true,
                                    element: <PersonnelNotesPage />,
                                },
                                {
                                    path: ":noteId",
                                    element: <PersonnelNotePage />,
                                },
                            ],
                        },
                        {
                            path: "roles",
                            children: [
                                {
                                    index: true,
                                    element: <PersonnelRolesPage />,
                                },
                                {
                                    path: ":personnelRoleId",
                                    element: <PersonnelRoleEditPage />,
                                },
                            ],
                        },
                        {
                            path: "activedirectory",
                            element: <PersonnelActiveDirectoryPage />,
                        },
                        {
                            path: "openclinica",
                            element: <PersonnelOCRolesPage />,
                        },
                        {
                            path: "permissions",
                            element: <PersonnelPermissionsPage />,
                        },
                        {
                            path: "items",
                            element: <PersonnelFilesPage />,
                        },
                        {
                            path: "hubspot",
                            element: <PersonnelHubspotPage />,
                        },
                    ],
                },
            ],
        },
    ];

    return routes;
};

const createSettingsRoutes = (_user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "settings",
            element: (
                <>
                    <SettingsSubMenu />
                    <Outlet />
                </>
            ),
            children: [
                {
                    index: true,
                    element: <SettingsPageRedirect />,
                },
                {
                    path: "general",
                    element: (
                        <>
                            <GeneralSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <GeneralSettingsPageRedirect />,
                        },
                        {
                            path: "countries",
                            children: [
                                {
                                    index: true,
                                    element: <CountriesPage />,
                                },
                                {
                                    path: "create",
                                    element: <CountryPage />,
                                },
                                {
                                    path: ":countryId",
                                    element: <CountryPage />,
                                },
                            ],
                        },
                        {
                            path: "states",
                            children: [
                                {
                                    index: true,
                                    element: <StatesPage />,
                                },
                                {
                                    path: ":stateId",
                                    element: <StatePage />,
                                },
                            ],
                        },
                        {
                            path: "itemcomponenttypes",
                            children: [
                                {
                                    index: true,
                                    element: <ItemComponentTypesPage />,
                                },
                                {
                                    path: ":itemComponentTypeId",
                                    element: <ItemComponentTypePage />,
                                },
                            ],
                        },
                        {
                            path: "notetypes",
                            children: [
                                {
                                    index: true,
                                    element: <NoteTypesPage />,
                                },
                                {
                                    path: ":noteTypeId",
                                    element: <NoteTypePage />,
                                },
                            ],
                        },
                        {
                            path: "genders",
                            children: [
                                {
                                    index: true,
                                    element: <GendersPage />,
                                },
                                {
                                    path: ":genderId",
                                    element: <GenderPage />,
                                },
                            ],
                        },
                        {
                            path: "interests",
                            children: [
                                {
                                    index: true,
                                    element: <InterestsPage />,
                                },
                                {
                                    path: ":interestId",
                                    element: <InterestPage />,
                                },
                            ],
                        },
                        {
                            path: "membershiptypes",
                            children: [
                                {
                                    index: true,
                                    element: <MembershipTypesPage />,
                                },
                                {
                                    path: ":membershipTypeId",
                                    element: <MembershipTypePage />,
                                },
                            ],
                        },
                        {
                            path: "professionaldisciplines",
                            children: [
                                {
                                    index: true,
                                    element: <ProfessionalDisciplinesPage />,
                                },
                                {
                                    path: ":professionalDisciplineId",
                                    element: <ProfessionalDisciplinePage />,
                                },
                            ],
                        },
                        {
                            path: "salutations",
                            children: [
                                {
                                    index: true,
                                    element: <SalutationsPage />,
                                },
                                {
                                    path: ":salutationId",
                                    element: <SalutationPage />,
                                },
                            ],
                        },
                        {
                            path: "roles",
                            children: [
                                {
                                    index: true,
                                    element: <RolesPage />,
                                },
                                {
                                    path: ":roleId",
                                    element: <RolePage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "hubspot",
                    element: (
                        <>
                            <HubspotSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <HubspotSettingsPageRedirect />,
                        },
                        {
                            path: "activemembers",
                            element: <HubspotActiveMembersPage />,
                        },
                        {
                            path: "import",
                            element: <HubspotExportPage />,
                        },
                        {
                            path: "emailupdate",
                            element: <HubspotMemberPage />,
                        },
                    ],
                },
                {
                    path: "openclinicauserroles",
                    element: <OpenClinicaUserRolesPage />,
                },
                {
                    path: "institution",
                    element: (
                        <>
                            <InstitutionSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <InstitutionSettingsPageRedirect />,
                        },
                        {
                            path: "sitelocations",
                            children: [
                                {
                                    index: true,
                                    element: <SiteLocationsPage />,
                                },
                                {
                                    path: ":siteLocationId",
                                    element: <SiteLocationPage />,
                                },
                            ],
                        },
                        {
                            path: "facilityalliancemembers",
                            children: [
                                {
                                    index: true,
                                    element: <FacilityAllianceMembersPage />,
                                },
                                {
                                    path: ":facilityAllianceMemberId",
                                    element: <FacilityAllianceMemberPage />,
                                },
                            ],
                        },
                        {
                            path: "institutiontypes",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionTypesPage />,
                                },
                                {
                                    path: ":institutionTypeId",
                                    element: <InstitutionTypePage />,
                                },
                            ],
                        },
                        {
                            path: "institutioncodings",
                            children: [
                                {
                                    index: true,
                                    element: <InstitutionCodingsPage />,
                                },
                                {
                                    path: ":institutionCodingId",
                                    element: <InstitutionCodingPage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "trial",
                    element: (
                        <>
                            <TrialSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <TrialSettingsPageRedirect />,
                        },
                        {
                            path: "trialcategorys",
                            children: [
                                {
                                    index: true,
                                    element: <TrialCategoriesPage />,
                                },
                                {
                                    path: ":trialCategoryId",
                                    element: <TrialCategoryPage />,
                                },
                            ],
                        },
                        {
                            path: "trialtypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialTypesPage />,
                                },
                                {
                                    path: ":trialTypeId",
                                    element: <TrialTypePage />,
                                },
                            ],
                        },
                        {
                            path: "trialphases",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPhasesPage />,
                                },
                                {
                                    path: ":trialPhaseId",
                                    element: <TrialPhasePage />,
                                },
                            ],
                        },
                        {
                            path: "tumourgroups",
                            children: [
                                {
                                    index: true,
                                    element: <TumourGroupsPage />,
                                },
                                {
                                    path: ":tumourGroupId",
                                    element: <TumourGroupPage />,
                                },
                            ],
                        },
                        {
                            path: "trialPresentationTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPresentationTypesPage />,
                                },
                                {
                                    path: ":trialPresentationTypeId",
                                    element: <TrialPresentationTypePage />,
                                },
                            ],
                        },
                        {
                            path: "trialPublicationTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialPublicationTypesPage />,
                                },
                                {
                                    path: ":trialPublicationTypeId",
                                    element: <TrialPublicationTypePage />,
                                },
                            ],
                        },
                        {
                            path: "trialAnalysisTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrialAnalysisTypesPage />,
                                },
                                {
                                    path: ":trialAnalysisTypeId",
                                    element: <TrialAnalysisTypePage />,
                                },
                            ],
                        },
                        {
                            path: "involvements",
                            children: [
                                {
                                    index: true,
                                    element: <InvolvementsPage />,
                                },
                                {
                                    path: ":involvementId",
                                    element: <InvolvementPage />,
                                },
                            ],
                        },
                        {
                            path: "allocatedwps",
                            children: [
                                {
                                    index: true,
                                    element: <AllocatedWPsPage />,
                                },
                                {
                                    path: ":allocatedWPId",
                                    element: <AllocatedWPPage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "qa",
                    element: (
                        <>
                            <QaSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <QaSettingsPageRedirect />,
                        },
                        {
                            path: "formtemplates",
                            children: [
                                {
                                    index: true,
                                    element: <FormTemplatesPage />,
                                },
                                {
                                    path: ":formTemplateId",
                                    element: <FormTemplatePage />,
                                },
                            ],
                        },
                        {
                            path: "qaEmailTemplates",
                            children: [
                                {
                                    index: true,
                                    element: <QAEmailTemplatesPage />,
                                },
                                {
                                    path: ":standardEmailTemplateId",
                                    element: <QAEmailTemplatePage />,
                                },
                            ],
                        },
                        {
                            path: "formcomponents",
                            children: [
                                {
                                    index: true,
                                    element: <QAItemComponentsPage />,
                                },
                                {
                                    path: ":itemComponentId",
                                    children: [
                                        {
                                            index: true,
                                            element: <QAItemComponentPage />,
                                        },
                                        {
                                            path: ":cloneId",
                                            element: <QAItemComponentPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "reportingTagCategories",
                            children: [
                                {
                                    index: true,
                                    element: <ReportingTagCategoriesPage />,
                                },
                                {
                                    path: ":reportingTagCategoryId",
                                    element: <ReportingTagCategoryPage />,
                                },
                            ],
                        },
                        {
                            path: "reportingTags",
                            children: [
                                {
                                    index: true,
                                    element: <ReportingTagsPage />,
                                },
                                {
                                    path: ":reportingTagId",
                                    element: <ReportingTagPage />,
                                },
                            ],
                        },
                        {
                            path: "taskTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TaskTypesPage />,
                                },
                                {
                                    path: ":taskTypeId",
                                    element: <TaskTypePage />,
                                },
                            ],
                        },
                        {
                            path: "trackerTypes",
                            children: [
                                {
                                    index: true,
                                    element: <TrackerTypesPage />,
                                },
                                {
                                    path: ":trackerTypeId",
                                    element: <TrackerTypePage />,
                                },
                            ],
                        },
                        {
                            path: "trackerMilestones",
                            children: [
                                {
                                    index: true,
                                    element: <TrackerMilestonesPage />,
                                },
                                {
                                    path: ":trackerMilestoneId",
                                    element: <TrackerMilestonePage />,
                                },
                            ],
                        },
                        {
                            path: "formTemplateFileTypes",
                            children: [
                                {
                                    index: true,
                                    element: <FormTemplateFileTypesPage />,
                                },
                                {
                                    path: ":formTemplateFileTypeId",
                                    element: <FormTemplateFileTypePage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "item",
                    element: (
                        <>
                            <ItemSettingsSubMenu />
                            <Outlet />
                        </>
                    ),
                    children: [
                        {
                            index: true,
                            element: <ItemSettingsPageRedirect />,
                        },
                        {
                            path: "itemcomponents",
                            children: [
                                {
                                    index: true,
                                    element: <ItemComponentsPage />,
                                },
                                {
                                    path: ":itemComponentId",
                                    children: [
                                        {
                                            index: true,
                                            element: <ItemComponentPage />,
                                        },
                                        {
                                            path: ":cloneId",
                                            element: <ItemComponentPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: "itemcategories",
                            children: [
                                {
                                    index: true,
                                    element: <CategoriesPage />,
                                },
                                {
                                    path: ":categoryId",
                                    element: <CategoryPage />,
                                },
                            ],
                        },
                        {
                            path: "itemtemplates",
                            children: [
                                {
                                    index: true,
                                    element: <TrialInstitutionItemTemplatesPage />,
                                },
                                {
                                    path: ":trialInstitutionItemTemplateId",
                                    element: <TrialInstitutionItemTemplatePage />,
                                },
                            ],
                        },
                        {
                            path: "trialitemtemplates",
                            children: [
                                {
                                    index: true,
                                    element: <TemplateTrialItemsPage />,
                                },
                                {
                                    path: ":templateTrialItemId",
                                    element: <TemplateTrialItemPage />,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ];

    return routes;
};

const createRootRoutes = (user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "dashboard",
            element: <DashboardPage />,
        },
        {
            path: "home",
            element: <HomePage />,
        },
        {
            path: "search",
            element: <SearchPage />,
        },
        {
            path: "error",
            children: [
                {
                    index: true,
                    element: <ErrorPage />,
                },
                {
                    path: ":errorCode",
                    element: <ErrorPage />,
                },
            ],
        },
        {
            path: "mytrials",
            element: <MyTrialsPage />,
        },
        ...createTrialRoutes(user),
        ...createInstitutionRoutes(user),
        ...createGroupRoutes(user),
        ...createPersonnelRoutes(user),
        ...createSettingsRoutes(user),
    ];

    return routes;
};

const initialLayoutState: ILayoutContext = {
    ...defaultLayoutContext,
    menuItems: mainNavigationItems,
};

export const createRoutes = (user?: User): RouteObject[] => {
    const routes: RouteObject[] = [
        {
            path: "/",
            element: (
                <LayoutProvider initialState={initialLayoutState}>
                    <DefaultLayout>
                        <Outlet />
                        {/* <AnalyticsTracker /> */}
                    </DefaultLayout>
                </LayoutProvider>
            ),
            children: [
                {
                    index: true,
                    element: <Navigate to="/dashboard" replace />,
                },
                ...createRootRoutes(user),
            ],
        },
    ];

    return routes;
};

export default createRoutes;
